import type {
	Feature,
	FeatureDependency,
	Role,
	Plan,
	Organization,
	OrganizationStats,
	OrganizationListResponse,
	OrganizationOverviewStats,
	OrganizationDetailStats,
	User,
	PermissionCheckResult,
	PermissionMatrix,
	EffectiveFeaturesResult,
	TestLabPreset,
	TestLabBatch,
	TestLabUserVerification,
	TestLabPayrollPreview,
	TestLabCleanupResult,
} from "@/types";
import { getAccessToken, getRefreshToken, storeTokens, clearAuthData } from "@/services/auth";
import { API_BASE_URL, ADMIN_API_URL } from "@/config";

const API_BASE = API_BASE_URL;

/**
 * Try to refresh the access token
 */
async function tryRefreshToken(): Promise<boolean> {
	const refreshToken = getRefreshToken();
	if (!refreshToken) return false;

	try {
		const response = await fetch(`${ADMIN_API_URL}/auth/refresh-token`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ refreshToken }),
		});

		if (!response.ok) return false;

		const data = await response.json();
		if (data.success && data.data) {
			storeTokens(data.data.accessToken, data.data.refreshToken);
			return true;
		}
		return false;
	} catch {
		return false;
	}
}

// Helper for authenticated admin API calls
export async function fetchAdminApi<T>(
	endpoint: string,
	options?: RequestInit,
): Promise<T> {
	const token = getAccessToken();

	const response = await fetch(`${API_BASE}/admin/api${endpoint}`, {
		...options,
		headers: {
			"Content-Type": "application/json",
			...(token ? { Authorization: `Bearer ${token}` } : {}),
			...options?.headers,
		},
	});

	// Handle 401 - try to refresh token
	if (response.status === 401) {
		const refreshed = await tryRefreshToken();
		if (refreshed) {
			// Retry the original request with new token
			const newToken = getAccessToken();
			const retryResponse = await fetch(`${API_BASE}/admin/api${endpoint}`, {
				...options,
				headers: {
					"Content-Type": "application/json",
					...(newToken ? { Authorization: `Bearer ${newToken}` } : {}),
					...options?.headers,
				},
			});

			if (!retryResponse.ok) {
				const error = await retryResponse.json().catch(() => ({}));
				throw new Error(error.message || `HTTP ${retryResponse.status}`);
			}

			const data = await retryResponse.json();
			if (data.success === false) {
				throw new Error(data.message || "API request failed");
			}

			return data.data ?? data;
		}
		// Refresh failed - clear auth data and redirect to login
		clearAuthData();
		window.location.href = "/login";
		throw new Error("Session expired. Please login again.");
	}

	if (!response.ok) {
		const error = await response.json().catch(() => ({}));
		throw new Error(error.message || `HTTP ${response.status}`);
	}

	const data = await response.json();
	if (data.success === false) {
		throw new Error(data.message || "API request failed");
	}

	return data.data ?? data;
}

// Helper to transform backend feature to frontend format
function transformFeature(feature: any): Feature {
	return {
		...feature,
		slug: feature.code || feature.slug, // Map code to slug
		module: feature.module || feature.category || "other",
		actions: feature.actions || ["view"],
		ui: {
			label: feature.name,
			...feature.ui,
		},
	};
}

// Features
export async function createFeature(data: {
	code: string;
	name: string;
	description?: string;
	module: string;
	actions: string[];
	scopeType?: "collective" | "personal" | "administrative";
}): Promise<Feature> {
	const response = await fetchAdminApi<any>("/v2/features", {
		method: "POST",
		body: JSON.stringify(data),
	});
	return transformFeature(response);
}

export async function getFeatures(): Promise<Feature[]> {
	const response = await fetchAdminApi<{ features: any[]; byCategory: any }>(
		"/plans/features",
	);
	// Backend returns {features: [...], byCategory: {...}}, extract features array
	const features = response.features || response;
	return Array.isArray(features) ? features.map(transformFeature) : [];
}

export async function getFeatureWithDependencies(
	featureCode: string,
): Promise<{
	feature: Feature;
	dependencies: Feature[];
	dependents: Feature[];
}> {
	const response = await fetchAdminApi<any>(
		`/plans/features/${featureCode}/dependencies`,
	);
	return {
		feature: transformFeature(response.feature),
		dependencies: (response.dependencies || []).map(transformFeature),
		dependents: (response.dependents || []).map(transformFeature),
	};
}

export async function getAllDependencies(): Promise<{
	features: Feature[];
	dependencyMap: Record<string, FeatureDependency[]>;
	reverseDependencyMap: Record<string, string[]>;
}> {
	const response = await fetchAdminApi<any>("/plans/features/dependencies");
	return {
		features: (response.features || []).map(transformFeature),
		dependencyMap: response.dependencyMap || {},
		reverseDependencyMap: response.reverseDependencyMap || {},
	};
}

// Roles
export async function getRoles(organizationId?: string): Promise<Role[]> {
	const query = organizationId ? `?organizationId=${organizationId}` : "";
	return fetchAdminApi<Role[]>(`/roles${query}`);
}

export async function getRole(roleId: string): Promise<Role> {
	return fetchAdminApi<Role>(`/roles/${roleId}`);
}

export async function createRole(
	data: Partial<Role>,
): Promise<{ role: Role }> {
	return fetchAdminApi("/roles", {
		method: "POST",
		body: JSON.stringify(data),
	});
}

export async function updateRole(
	roleId: string,
	data: Partial<Role>,
): Promise<Role> {
	return fetchAdminApi(`/roles/${roleId}`, {
		method: "PUT",
		body: JSON.stringify(data),
	});
}

export async function deleteRole(roleId: string): Promise<void> {
	return fetchAdminApi(`/roles/${roleId}`, {
		method: "DELETE",
	});
}

// Plans
export async function getPlans(): Promise<Plan[]> {
	return fetchAdminApi<Plan[]>("/plans");
}

export async function getPlanFeatureMatrix(): Promise<{
	plans: Plan[];
	features: Feature[];
	matrix: Record<string, Record<string, boolean>>;
}> {
	const response = await fetchAdminApi<any>("/plans/matrix");
	return {
		plans: (response.plans || []).map((plan: any) => ({
			...plan,
			// Keep slug as-is since matrix keys use plan.slug
		})),
		features: (response.features || []).map(transformFeature),
		matrix: response.matrix || {},
	};
}

export async function togglePlanFeature(
	planSlug: string,
	featureCode: string,
): Promise<{ enabled: boolean }> {
	return fetchAdminApi("/plans/toggle-feature", {
		method: "POST",
		body: JSON.stringify({ planSlug, featureCode }),
	});
}

// Organizations
/**
 * Every organization, A to Z. The endpoint is paginated (20 by default), and
 * the pickers on the Permission Matrix, 403 Debugger, Org Bootstrapper and
 * User Perspective pages, and the Dashboard count, all need the whole list:
 * reading only the first page hid every organization past the 20 newest.
 */
export async function getOrganizations(): Promise<Organization[]> {
	const all: Organization[] = [];
	for (let page = 1; page <= 50; page++) {
		const response = await fetchAdminApi<{
			organizations: Organization[];
			pagination?: { pages?: number };
		}>(`/organizations?page=${page}&limit=100&sortBy=name&sortOrder=asc`);
		all.push(...(response.organizations || []));
		if (page >= (response.pagination?.pages ?? 1)) break;
	}
	// The database sorts case-sensitively ("bondnavy" after "Zeta"); sort the
	// way people read.
	return all.sort((a, b) =>
		a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
	);
}

export async function getOrganization(orgId: string): Promise<Organization> {
	return fetchAdminApi<Organization>(`/organizations/${orgId}`);
}

export async function getEffectiveFeatures(
	orgId: string,
): Promise<EffectiveFeaturesResult> {
	return fetchAdminApi(`/organizations/${orgId}/effective-features`);
}

export async function updateCustomPackage(
	orgId: string,
	data: {
		enabled?: boolean;
		customFeatures?: string[];
		blockedFeatures?: string[];
	},
): Promise<Organization> {
	return fetchAdminApi(`/organizations/${orgId}/custom-package`, {
		method: "PATCH",
		body: JSON.stringify(data),
	});
}

// Attendance Seats (GPS attendance add-on, manual/comped billing — no Stripe)
export interface AttendanceSeats {
	purchased: number;
	assigned: number;
	assignedUsers?: Array<{
		_id: string;
		firstName?: string;
		lastName?: string;
		email?: string;
		employeeId?: string;
		photoUrl?: string;
	}>;
}

export async function getAttendanceSeats(
	orgId: string,
): Promise<AttendanceSeats> {
	return fetchAdminApi(`/organizations/${orgId}/attendance-seats`);
}

export async function updateAttendanceSeats(
	orgId: string,
	purchased: number,
): Promise<AttendanceSeats> {
	return fetchAdminApi(`/organizations/${orgId}/attendance-seats`, {
		method: "PATCH",
		body: JSON.stringify({ purchased }),
	});
}

// GPS seat holders — 30-day lock and bound phone. Only FlowMetrics can release
// either early; organizations cannot, which is what stops one seat being
// rotated round a workforce.
export interface GpsSeatHolder {
	_id: string;
	firstName?: string;
	lastName?: string;
	email?: string;
	employeeId?: string;
	/** Left the organization but still holds the seat (inside the lock). */
	departed: boolean;
	seatLockedUntil: string | null;
	boundDevice: {
		platform?: string | null;
		deviceModel?: string | null;
		osVersion?: string | null;
		appVersion?: string | null;
		boundAt: string;
		lastSeenAt: string;
	} | null;
}

export interface GpsSeatHolders {
	purchased: number;
	assigned: number;
	holders: GpsSeatHolder[];
}

export async function getGpsSeatHolders(
	orgId: string,
): Promise<GpsSeatHolders> {
	return fetchAdminApi(`/organizations/${orgId}/gps-seats`);
}

export async function releaseGpsSeat(
	orgId: string,
	userId: string,
	reason: string,
): Promise<void> {
	return fetchAdminApi(`/organizations/${orgId}/gps-seats/${userId}/release`, {
		method: "POST",
		body: JSON.stringify({ reason }),
	});
}

export async function releaseMobileDevice(
	orgId: string,
	userId: string,
	reason: string,
): Promise<void> {
	return fetchAdminApi(
		`/organizations/${orgId}/mobile-devices/${userId}/release`,
		{ method: "POST", body: JSON.stringify({ reason }) },
	);
}

/** One employee's bound phone. Everyone on the mobile app is bound. */
export interface BoundPhone {
	userId: string;
	name: string;
	email?: string;
	employeeId?: string;
	platform?: string;
	deviceModel?: string;
	appVersion?: string;
	boundAt: string;
	lastSeenAt?: string;
	/** From this date a new phone replaces this one automatically. */
	changeableFrom: string;
}

export async function getBoundPhones(orgId: string): Promise<BoundPhone[]> {
	return fetchAdminApi(`/organizations/${orgId}/mobile-devices`);
}

// Global Settings (system-admin key/value)
export interface AdminSettings {
	stripe_attendance_price_id_monthly: string;
	stripe_attendance_price_id_yearly: string;
}

export async function getAdminSettings(): Promise<AdminSettings> {
	return fetchAdminApi("/settings");
}

export async function updateAdminSetting(
	key: keyof AdminSettings,
	value: string,
): Promise<{ key: string; value: string }> {
	return fetchAdminApi("/settings", {
		method: "PATCH",
		body: JSON.stringify({ key, value }),
	});
}

// Users
export async function getUsers(organizationId: string): Promise<User[]> {
	const response = await fetchAdminApi<{ users: User[]; pagination: any }>(
		`/organizations/${organizationId}/users`,
	);
	// Backend returns { users, pagination }, extract just the users array
	return response.users || [];
}

// Permission Checking
export async function checkPermission(data: {
	userId: string;
	organizationId: string;
	feature: string;
	action: string;
}): Promise<PermissionCheckResult> {
	return fetchAdminApi("/permissions/check", {
		method: "POST",
		body: JSON.stringify(data),
	});
}

export async function debugPermission(data: {
	userId: string;
	organizationId: string;
	feature: string;
	action: string;
}): Promise<{
	result: PermissionCheckResult;
	roleChain: Array<{
		roleId: string;
		roleName: string;
		hasFeature: boolean;
		hasAction: boolean;
	}>;
	planCheck: {
		plan: string;
		hasFeature: boolean;
		inBasePlan: boolean;
		customPackage?: {
			enabled: boolean;
			isCustomAdded: boolean;
			isBlocked: boolean;
		};
	};
	subscriptionCheck: {
		status: string;
		isActive: boolean;
	};
	suggestions: string[];
}> {
	return fetchAdminApi("/permissions/debug", {
		method: "POST",
		body: JSON.stringify(data),
	});
}

export async function getUserRoleSummary(
	userId: string,
	organizationId: string,
): Promise<{
	roles: Role[];
	effectiveScope: string;
	permissionCount: number;
}> {
	return fetchAdminApi(
		`/users/${userId}/role-summary?organizationId=${organizationId}`,
	);
}

// Permission Matrix
export async function getPermissionMatrix(
	organizationId?: string,
): Promise<PermissionMatrix> {
	const path = organizationId
		? `/permissions/matrix/${organizationId}`
		: "/permissions/matrix";
	return fetchAdminApi<PermissionMatrix>(path);
}

export async function togglePermission(data: {
	roleId: string;
	featureCode: string;
	action: string;
	organizationId?: string;
}): Promise<{ actions: string[] }> {
	return fetchAdminApi("/permissions/toggle", {
		method: "POST",
		body: JSON.stringify(data),
	});
}

// Menu Items
export async function getMenuItems(includeHidden = false): Promise<any[]> {
	return fetchAdminApi(`/menu-items?includeHidden=${includeHidden}`);
}

export async function getMenuStructure(): Promise<{
	sections: string[];
	structure: Record<string, any[]>;
	totalItems: number;
}> {
	return fetchAdminApi("/menu-items/structure");
}

export async function getMenuItem(code: string): Promise<any> {
	return fetchAdminApi(`/menu-items/${code}`);
}

export async function createMenuItem(data: {
	code: string;
	label: string;
	icon?: string;
	path?: string;
	parentCode?: string;
	order?: number;
	featureCode?: string;
	isHidden?: boolean;
	isSection?: boolean;
}): Promise<any> {
	return fetchAdminApi("/menu-items", {
		method: "POST",
		body: JSON.stringify(data),
	});
}

export async function updateMenuItem(
	code: string,
	data: Partial<{
		label: string;
		icon: string;
		path: string;
		parentCode: string;
		order: number;
		featureCode: string;
		isHidden: boolean;
		isSection: boolean;
	}>,
): Promise<any> {
	return fetchAdminApi(`/menu-items/${code}`, {
		method: "PUT",
		body: JSON.stringify(data),
	});
}

export async function deleteMenuItem(code: string): Promise<void> {
	return fetchAdminApi(`/menu-items/${code}`, {
		method: "DELETE",
	});
}

export async function reorderMenuItems(
	items: Array<{ code: string; order: number }>,
): Promise<{ updated: number }> {
	return fetchAdminApi("/menu-items/reorder", {
		method: "POST",
		body: JSON.stringify({ items }),
	});
}

export async function getMenuSections(): Promise<Array<{
	code: string;
	label: string;
	icon?: string;
	order: number;
	isSection: boolean;
}>> {
	return fetchAdminApi("/menu-items/sections");
}

export async function getMenuPreview(roleId?: string): Promise<{
	items: any[];
	totalItems: number;
	visibleItems: number;
	roleId?: string;
}> {
	const query = roleId ? `?roleId=${roleId}` : "";
	return fetchAdminApi(`/menu-items/preview${query}`);
}

// ==========================================
// System Admin API - Organizations
// ==========================================

// Organization endpoints
export async function getAdminOrganizations(params?: {
	page?: number;
	limit?: number;
	search?: string;
	plan?: string;
	subscriptionStatus?: string;
	sortBy?: string;
	sortOrder?: "asc" | "desc";
}): Promise<OrganizationListResponse> {
	const queryParams = new URLSearchParams();
	if (params?.page) queryParams.append("page", String(params.page));
	if (params?.limit) queryParams.append("limit", String(params.limit));
	if (params?.search) queryParams.append("search", params.search);
	if (params?.plan) queryParams.append("plan", params.plan);
	if (params?.subscriptionStatus)
		queryParams.append("subscriptionStatus", params.subscriptionStatus);
	if (params?.sortBy) queryParams.append("sortBy", params.sortBy);
	if (params?.sortOrder) queryParams.append("sortOrder", params.sortOrder);

	const query = queryParams.toString();
	return fetchAdminApi(`/organizations${query ? `?${query}` : ""}`);
}

export async function getAdminOrganization(
	id: string,
): Promise<Organization & { stats: OrganizationStats }> {
	return fetchAdminApi(`/organizations/${id}`);
}

export interface CreateOrganizationPayload {
	organizationName: string;
	slug?: string;
	plan: "free" | "starter" | "professional" | "enterprise";
	purchasedSeats?: number;
	owner: {
		firstName: string;
		lastName: string;
		email: string;
		password: string;
		confirmPassword: string;
	};
	customPackage?: {
		enabled: boolean;
		customFeatures?: string[];
		blockedFeatures?: string[];
		customPricing?: {
			price: number;
			currency: string;
			billingPeriod: "monthly" | "yearly";
			customTerms?: string;
		};
		notes?: string;
		effectiveDate?: string;
	};
}

export interface CreateOrganizationResponse {
	organization: Organization;
	owner: User;
	onboarding: {
		rolesCreated: string[];
		featuresEnabled: number;
		roleFeatureMapsCreated: number;
	};
}

export async function createAdminOrganization(
	data: CreateOrganizationPayload,
): Promise<CreateOrganizationResponse> {
	return fetchAdminApi("/organizations", {
		method: "POST",
		body: JSON.stringify(data),
	});
}

export async function updateAdminOrganization(
	id: string,
	data: {
		name?: string;
		slug?: string;
		purchasedSeats?: number;
	},
): Promise<Organization> {
	return fetchAdminApi(`/organizations/${id}`, {
		method: "PUT",
		body: JSON.stringify(data),
	});
}

export async function updateOrganizationStatus(
	id: string,
	subscriptionStatus: string,
): Promise<Organization> {
	return fetchAdminApi(`/organizations/${id}/status`, {
		method: "PATCH",
		body: JSON.stringify({ subscriptionStatus }),
	});
}

export async function updateOrganizationPlan(
	id: string,
	plan: string,
): Promise<Organization> {
	return fetchAdminApi(`/organizations/${id}/plan`, {
		method: "PATCH",
		body: JSON.stringify({ plan }),
	});
}

export async function getOrganizationUsers(
	id: string,
	params?: {
		page?: number;
		limit?: number;
		search?: string;
		isActive?: boolean;
	},
): Promise<{
	users: User[];
	pagination: {
		total: number;
		page: number;
		limit: number;
		pages: number;
	};
}> {
	const queryParams = new URLSearchParams();
	if (params?.page) queryParams.append("page", String(params.page));
	if (params?.limit) queryParams.append("limit", String(params.limit));
	if (params?.search) queryParams.append("search", params.search);
	if (typeof params?.isActive === "boolean")
		queryParams.append("isActive", String(params.isActive));

	const query = queryParams.toString();
	return fetchAdminApi(`/organizations/${id}/users${query ? `?${query}` : ""}`);
}

export async function getOrganizationDetailStats(
	id: string,
): Promise<OrganizationDetailStats> {
	return fetchAdminApi(`/organizations/${id}/stats`);
}

export interface CreateOrganizationUserPayload {
	email: string;
	firstName: string;
	lastName?: string;
	password: string;
	role?: "employee" | "manager" | "admin" | "owner";
	employmentType?: "PERMANENT" | "CONTRACT" | "INTERN";
}

export interface CreateOrganizationUserResponse {
	user: {
		_id: string;
		email: string;
		firstName: string;
		lastName: string;
		organizationId: string;
		role: string;
		employmentType: string | null;
	};
}

export async function createOrganizationUser(
	organizationId: string,
	data: CreateOrganizationUserPayload,
): Promise<CreateOrganizationUserResponse> {
	return fetchAdminApi(`/organizations/${organizationId}/users`, {
		method: "POST",
		body: JSON.stringify(data),
	});
}

export interface DeleteOrganizationResponse {
	organizationId: string;
	organizationName: string;
	deletedCounts: Record<string, number>;
	totalDeleted: number;
}

export async function deleteAdminOrganization(
	id: string,
): Promise<DeleteOrganizationResponse> {
	return fetchAdminApi(`/organizations/${id}`, {
		method: "DELETE",
	});
}

export async function getOrganizationsOverview(): Promise<OrganizationOverviewStats> {
	return fetchAdminApi("/organizations/overview");
}

// ==========================================
// User Perspective Simulation
// ==========================================

export interface UserPerspective {
	user: {
		_id: string;
		email: string;
		firstName?: string;
		lastName?: string;
		role?: string;
	};
	organization: {
		_id: string;
		name: string;
		slug: string;
		plan: string;
		customPackage: {
			enabled: boolean;
			customFeaturesCount: number;
			blockedFeaturesCount: number;
		};
	};
	roles: {
		mongodb: Array<{
			_id: string;
			name: string;
			displayName?: string;
			isGlobal: boolean;
			dataAccessScope?: string;
		}>;
	};
	plan: {
		name: string;
		featuresCount: number;
		features: string[];
	};
	effectiveFeatures: string[];
	blockedFeatures: string[];
	permissions: Array<{
		feature: string;
		featureName: string;
		module: string;
		actions: Record<string, boolean>;
		source: "role" | "plan" | "custom_package";
	}>;
	permissionsByModule: Record<string, Array<{
		feature: string;
		featureName: string;
		module: string;
		actions: Record<string, boolean>;
		source: "role" | "plan" | "custom_package";
	}>>;
	menu: {
		bySection: Record<string, Array<{
			code: string;
			label: string;
			icon?: string;
			route?: string;
			section?: string;
			order: number;
			visible: boolean;
			reason?: string;
			children?: any[];
		}>>;
		allItems: Array<{
			code: string;
			label: string;
			icon?: string;
			route?: string;
			section?: string;
			order: number;
			visible: boolean;
			reason?: string;
			children?: any[];
		}>;
	};
	summary: {
		totalFeatures: number;
		effectiveFeatures: number;
		featuresWithView: number;
		featuresWithCreate: number;
		featuresWithUpdate: number;
		featuresWithDelete: number;
		visibleMenuItems: number;
		hiddenMenuItems: number;
	};
	generatedAt: string;
}

export async function getUserPerspective(userId: string): Promise<UserPerspective> {
	return fetchAdminApi(`/users/${userId}/perspective`);
}

export async function checkUserPermission(
	userId: string,
	feature: string,
	action: string,
): Promise<{ user: { _id: string; email: string }; check: { feature: string; action: string; allowed: boolean } }> {
	return fetchAdminApi(`/users/${userId}/check-permission`, {
		method: "POST",
		body: JSON.stringify({ feature, action }),
	});
}

// ==========================================
// Custom Package Management
// ==========================================

export interface CustomPackageData {
	organizationId: string;
	organizationName: string;
	plan: string;
	customPackage: {
		enabled: boolean;
		customFeatures?: string[];
		blockedFeatures?: string[];
		customPricing?: {
			price: number;
			currency: string;
			billingPeriod: "monthly" | "yearly";
			customTerms?: string;
		};
		notes?: string;
		effectiveDate?: string;
	} | null;
}

export interface CustomPackageInput {
	enabled: boolean;
	customFeatures?: string[];
	blockedFeatures?: string[];
	customPricing?: {
		price: number;
		currency: string;
		billingPeriod: "monthly" | "yearly";
		customTerms?: string;
	};
	notes?: string;
	effectiveDate?: string;
}

export interface ValidationResult {
	valid: boolean;
	errors: string[];
	warnings: string[];
	preview?: {
		basePlanFeatures: string[];
		customFeatures: string[];
		blockedFeatures: string[];
		effectiveFeatures: string[];
		featureCount: {
			base: number;
			custom: number;
			blocked: number;
			effective: number;
		};
	};
}

// Get custom package for organization
export async function getCustomPackage(orgId: string): Promise<CustomPackageData> {
	return fetchAdminApi(`/organizations/${orgId}/custom-package`);
}

// Add custom features
export async function addCustomFeatures(
	orgId: string,
	featureCodes: string[],
): Promise<{ organizationId: string; customPackage: CustomPackageData["customPackage"] }> {
	return fetchAdminApi(`/organizations/${orgId}/custom-package/features/add`, {
		method: "POST",
		body: JSON.stringify({ featureCodes }),
	});
}

// Block features
export async function blockFeatures(
	orgId: string,
	featureCodes: string[],
): Promise<{ organizationId: string; customPackage: CustomPackageData["customPackage"] }> {
	return fetchAdminApi(`/organizations/${orgId}/custom-package/features/block`, {
		method: "POST",
		body: JSON.stringify({ featureCodes }),
	});
}

// Remove feature from custom/blocked list
export async function removeFeature(
	orgId: string,
	featureCode: string,
): Promise<{ organizationId: string; customPackage: CustomPackageData["customPackage"] }> {
	return fetchAdminApi(`/organizations/${orgId}/custom-package/features/${featureCode}`, {
		method: "DELETE",
	});
}

// Reset to base plan
export async function resetToBasePlan(
	orgId: string,
): Promise<{ organizationId: string; plan: string; customPackage: null }> {
	return fetchAdminApi(`/organizations/${orgId}/custom-package/reset`, {
		method: "POST",
	});
}

// Validate custom package (dry-run)
export async function validateCustomPackage(
	orgId: string,
	data: CustomPackageInput,
): Promise<ValidationResult> {
	return fetchAdminApi(`/organizations/${orgId}/custom-package/validate`, {
		method: "POST",
		body: JSON.stringify(data),
	});
}

// ==========================================
// Feature Dependency Management
// ==========================================

export interface FeatureDependencyInput {
	feature: string;
	action: string;
	reason?: string;
}

export interface DependencyValidationResult {
	valid: boolean;
	errors: Array<{
		type: "circular" | "missing_feature" | "invalid_action";
		feature: string;
		message: string;
		chain?: string[];
	}>;
	warnings: Array<{
		type: "unused" | "orphan";
		feature: string;
		message: string;
	}>;
}

/**
 * Update a feature
 */
export async function updateFeature(
	code: string,
	data: {
		name?: string;
		description?: string;
		module?: string;
		actions?: string[];
		scopeType?: "collective" | "personal" | "administrative";
		isActive?: boolean;
	},
): Promise<Feature> {
	const response = await fetchAdminApi<any>(`/v2/features/${code}`, {
		method: "PUT",
		body: JSON.stringify(data),
	});
	return transformFeature(response);
}

/**
 * Delete a feature
 */
export async function deleteFeature(code: string): Promise<void> {
	await fetchAdminApi(`/v2/features/${code}`, {
		method: "DELETE",
	});
}

/**
 * Update all dependencies for a feature
 */
export async function updateFeatureDependencies(
	featureCode: string,
	requires: FeatureDependencyInput[],
): Promise<Feature> {
	return fetchAdminApi(`/v2/features/${featureCode}/dependencies`, {
		method: "PUT",
		body: JSON.stringify({ requires }),
	});
}

/**
 * Add a single dependency to a feature
 */
export async function addFeatureDependency(
	featureCode: string,
	dependency: FeatureDependencyInput,
): Promise<Feature> {
	return fetchAdminApi(`/v2/features/${featureCode}/dependencies`, {
		method: "POST",
		body: JSON.stringify(dependency),
	});
}

/**
 * Remove a dependency from a feature
 */
export async function removeFeatureDependency(
	featureCode: string,
	depFeature: string,
	action: string,
): Promise<Feature> {
	return fetchAdminApi(`/v2/features/${featureCode}/dependencies`, {
		method: "DELETE",
		body: JSON.stringify({ feature: depFeature, action }),
	});
}

/**
 * Validate all feature dependencies (check for circular deps, missing features)
 */
export async function validateAllDependencies(): Promise<DependencyValidationResult> {
	return fetchAdminApi("/v2/features/dependencies/validate");
}

// ==========================================
// Permission Preview & Dependency Resolution
// ==========================================

export interface PermissionInput {
	feature: string;
	actions: string[];
}

export interface AutoGrantedPermission {
	feature: string;
	action: string;
	requiredBy: string;
	reason?: string;
}

export interface DependencyResolutionResult {
	resolvedPermissions: PermissionInput[];
	autoGranted: AutoGrantedPermission[];
	warnings: string[];
	errors: string[];
}

export interface DependencyTreeNode {
	feature: string;
	action?: string;
	reason?: string;
	circular?: boolean;
	dependencies: DependencyTreeNode[];
}

/**
 * Preview resolved permissions including auto-granted dependencies
 */
export async function previewPermissions(
	permissions: PermissionInput[],
): Promise<DependencyResolutionResult> {
	return fetchAdminApi("/permissions/preview", {
		method: "POST",
		body: JSON.stringify({ permissions }),
	});
}

/**
 * Get the dependency tree for a specific feature
 */
export async function getFeatureDependencyTree(
	featureCode: string,
): Promise<DependencyTreeNode | null> {
	return fetchAdminApi(`/plans/features/${featureCode}/dependencies`);
}

/**
 * Get all features with their dependency information
 * Returns features with requires array and dependents (reverse lookup)
 */
export async function getFeaturesWithDependencyInfo(): Promise<{
	features: Feature[];
	dependencyMap: Record<string, Array<{ feature: string; action: string; reason?: string }>>;
	reverseDependencyMap: Record<string, string[]>;
}> {
	const response = await fetchAdminApi<any>("/plans/features/dependencies");
	return {
		features: (response.features || []).map(transformFeature),
		dependencyMap: response.dependencyMap || {},
		reverseDependencyMap: response.reverseDependencyMap || {},
	};
}

/**
 * Validate all feature dependencies for circular refs and missing features
 */
export async function validateFeatureDependenciesApi(): Promise<{
	valid: boolean;
	circularDependencies: string[][];
	missingFeatures: { feature: string; missingDep: string }[];
}> {
	return fetchAdminApi("/plans/features/dependencies/validate");
}

/**
 * Update feature scope type
 */
export async function updateFeatureScopeType(
	featureCode: string,
	scopeType: "collective" | "personal" | "administrative",
): Promise<Feature> {
	const response = await fetchAdminApi<any>(`/plans/features/${featureCode}/scope-type`, {
		method: "PATCH",
		body: JSON.stringify({ scopeType }),
	});
	return transformFeature(response);
}

// ==========================================
// Currency Management
// ==========================================

export interface Currency {
	_id: string;
	code: string;
	name: string;
	symbol: string;
	numericCode?: string;
	decimalDigits: number;
	isActive: boolean;
}

export async function getCurrencies(): Promise<Currency[]> {
	const response = await fetchAdminApi<{ currencies: Currency[] }>("/currencies");
	return response.currencies || [];
}

// ==========================================
// Import / Export
// ==========================================

export async function getImportExportCounts(): Promise<{
	features: number;
	roles: number;
	plans: number;
	menuItems: number;
}> {
	return fetchAdminApi("/import-export/counts");
}

export async function exportData(
	type: "features" | "role-permissions" | "plans" | "menu-items" | "all",
): Promise<unknown> {
	return fetchAdminApi(`/import-export/export/${type}`);
}

export async function importData(
	type: "features" | "role-permissions" | "plans" | "menu-items" | "all",
	body: unknown,
): Promise<unknown> {
	return fetchAdminApi(`/import-export/import/${type}`, {
		method: "POST",
		body: JSON.stringify(body),
	});
}

// ==================== Test Lab ====================

export async function getTestLabPresets(): Promise<TestLabPreset[]> {
	return fetchAdminApi("/test-lab/presets");
}

export async function generateTestData(data: {
	organizationId: string;
	userIds: string[];
	startDate: string;
	endDate: string;
	mode: "preset" | "custom";
	presetName?: string;
	distribution?: { present: number; late: number; absent: number; earlyCheckout: number; leavePending: number };
	productivityPercent?: number;
}): Promise<TestLabBatch> {
	return fetchAdminApi("/test-lab/generate", {
		method: "POST",
		body: JSON.stringify(data),
	});
}

export async function finalizeTestData(batchId: string): Promise<TestLabBatch> {
	return fetchAdminApi(`/test-lab/finalize/${batchId}`, { method: "POST" });
}

export async function verifyTestData(batchId: string): Promise<TestLabUserVerification[]> {
	return fetchAdminApi(`/test-lab/verify/${batchId}`, { method: "POST" });
}

export async function getVerificationResult(batchId: string): Promise<{ perUser: TestLabUserVerification[] }> {
	return fetchAdminApi(`/test-lab/verify/${batchId}`);
}

export async function getPayrollPreview(batchId: string): Promise<TestLabPayrollPreview[]> {
	return fetchAdminApi(`/test-lab/payroll-preview/${batchId}`, { method: "POST" });
}

export async function commitPayroll(batchId: string): Promise<void> {
	return fetchAdminApi(`/test-lab/payroll-commit/${batchId}`, { method: "POST" });
}

export async function cleanupTestData(batchId: string): Promise<TestLabCleanupResult> {
	return fetchAdminApi(`/test-lab/cleanup/${batchId}`, { method: "DELETE" });
}

export async function getTestLabBatches(organizationId?: string): Promise<TestLabBatch[]> {
	const params = organizationId ? `?organizationId=${organizationId}` : "";
	return fetchAdminApi(`/test-lab/batches${params}`);
}

export async function getTestLabBatch(batchId: string): Promise<TestLabBatch> {
	return fetchAdminApi(`/test-lab/batches/${batchId}`);
}

